// FieldShell — shared chrome for the canonical basic field primitives
// (comp-ε GAP-3b-i, CHG-2433; a11y wiring CHG-2448). Renders the label
// (+ required marker), the control (children), and helperText / error,
// matching the stub chrome in _FieldStub.tsx but for REAL primitives (no
// data-field-stub marker). The box-style controls (text / textarea / select /
// number) share fieldControlStyle; checkbox composes its own inline layout.
//
// a11y (§2): FieldShell owns the error/helper text nodes and applies the
// caller-supplied errorId / helperId to them (error is a role="alert" live
// region). Each primitive generates the id triplet via useFieldIds(), wires its
// control's aria-describedby to error+helper via describedBy(), and points its
// label at inputId — so the input is programmatically linked to BOTH its error
// and its helper text (WCAG 2.1 SC 1.3.1 / 3.3.1 / 4.1.2).
// Doctrine: feedback_field_primitive_components_canonical.md.

import { useId, type CSSProperties, type ReactNode } from "react";

export interface FieldIds {
  inputId: string;
  errorId: string;
  helperId: string;
}

/** Generate the stable id triplet (control + error + helper) for one field. */
export function useFieldIds(): FieldIds {
  const base = useId();
  return { inputId: base, errorId: `${base}-error`, helperId: `${base}-help` };
}

/**
 * The aria-describedby value linking a control to whichever of its error /
 * helper text nodes are currently rendered. Returns undefined when neither is
 * present (so the attribute is omitted rather than pointing at a missing id).
 */
export function describedBy(
  ids: FieldIds,
  error?: string,
  helperText?: string,
): string | undefined {
  return (
    [error ? ids.errorId : null, helperText ? ids.helperId : null]
      .filter(Boolean)
      .join(" ") || undefined
  );
}

export function fieldControlStyle(hasError: boolean): CSSProperties {
  return {
    height: 36,
    padding: "0 12px",
    borderRadius: "var(--radius-md)",
    border: hasError
      ? "1px solid var(--color-error)"
      : "1px solid var(--color-border)",
    background: "var(--color-surface)",
    fontSize: 13,
    color: "var(--color-text)",
    width: "100%",
    boxSizing: "border-box",
  };
}

export function FieldShell({
  htmlFor,
  label,
  required,
  error,
  helperText,
  errorId,
  helperId,
  children,
}: {
  htmlFor: string;
  label?: string;
  required?: boolean;
  error?: string;
  helperText?: string;
  /** id applied to the error node so the control can aria-describedby it. */
  errorId: string;
  /** id applied to the helper node so the control can aria-describedby it. */
  helperId: string;
  children: ReactNode;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {label ? (
        <label
          htmlFor={htmlFor}
          style={{ fontSize: 12, color: "var(--color-text-muted)" }}
        >
          {label}{" "}
          {required ? (
            <span aria-hidden="true" style={{ color: "var(--color-error)" }}>
              *
            </span>
          ) : null}
        </label>
      ) : null}
      {children}
      {helperText ? (
        <small id={helperId} style={{ color: "var(--color-text-muted)" }}>
          {helperText}
        </small>
      ) : null}
      {error ? (
        <small id={errorId} role="alert" style={{ color: "var(--color-error)" }}>
          {error}
        </small>
      ) : null}
    </div>
  );
}
